<template>
    <b-modal ref="modal" size="md" centered hide-header hide-footer @hidden="onHidden" @shown="onShown" class="evidence-force-modal">
        <template v-if="error && error.canForceConfirm">
            <div class="evidence-modal-content">
                <div class="evidence-modal-header">
                    <div class="evidence-modal-icon">
                        <i class="fas fa-exclamation-triangle"></i>
                    </div>
                    <h5 class="evidence-modal-title">{{ title }}</h5>
                    <p class="evidence-modal-subtitle">{{ subtitle }}</p>
                </div>
                <div class="evidence-modal-detail-box">
                    <div class="evidence-modal-detail-title">
                        <span class="evidence-modal-bullet"></span>
                        {{ detailTitle }}
                    </div>
                    <p class="evidence-modal-detail-text">{{ error.message }}</p>
                    <p class="evidence-modal-warning">{{ warningText }}</p>
                </div>
                <p v-if="expectedForceConfirmValue" class="evidence-modal-instruction">
                    강제로 진행하려면
                    <span class="evidence-modal-confirm-highlight">{{ expectedForceConfirmValue }}</span
                    >을 입력하세요.
                </p>
                <b-form-input
                    ref="forceConfirmInputRef"
                    v-model="forceConfirmInput"
                    :placeholder="expectedForceConfirmValue + ' 입력'"
                    class="evidence-modal-input"
                    autocomplete="off"
                    @keyup.enter="handleConfirm"
                />
                <div class="evidence-modal-footer">
                    <b-button variant="outline-secondary" size="sm" class="evidence-modal-footer-btn" @click="hide">
                        취소
                    </b-button>
                    <b-button
                        variant="warning"
                        size="sm"
                        class="evidence-modal-footer-btn"
                        :disabled="forceConfirmInput !== expectedForceConfirmValue"
                        @click="handleConfirm"
                    >
                        {{ confirmButtonText }}
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { parseForceConfirmFromHint } from "../../utils/api.js";

export default {
    name: "EvidenceForceConfirmModal",
    props: {
        /** EVIDENCE_MISSING 에러 객체 { code, message, hint, canForceConfirm, details } */
        error: {
            type: Object,
            default: null,
        },
        /** 모달 제목 */
        title: {
            type: String,
            default: "강제생성 확인",
        },
        /** 서브타이틀 */
        subtitle: {
            type: String,
            default: "데이터 완정성 오류가 감지되었습니다.",
        },
        /** 상세 박스 제목 */
        detailTitle: {
            type: String,
            default: "누락된 항목 상세",
        },
        /** 경고 문구 */
        warningText: {
            type: String,
            default:
                "모든 필수 데이터가 입력되지 않은 상태에서 생성을 강행하면 데이터 신뢰도 점수가 하락할 수 있습니다.",
        },
        /** 확인 버튼 텍스트 */
        confirmButtonText: {
            type: String,
            default: "강제생성",
        },
    },
    data() {
        return {
            forceConfirmInput: "",
        };
    },
    computed: {
        expectedForceConfirmValue() {
            return parseForceConfirmFromHint(this.error?.hint);
        },
    },
    watch: {
        error: {
            handler(val) {
                if (!val) this.forceConfirmInput = "";
            },
        },
    },
    methods: {
        show() {
            this.forceConfirmInput = "";
            this.$refs.modal?.show();
        },
        hide() {
            this.$refs.modal?.hide();
        },
        handleConfirm() {
            if (this.forceConfirmInput !== this.expectedForceConfirmValue) return;
            this.$emit("confirm", this.expectedForceConfirmValue);
        },
        onHidden() {
            this.forceConfirmInput = "";
            this.$emit("hidden");
        },
        onShown() {
            if (this.error?.canForceConfirm) {
                this.$nextTick(() => {
                    this.$refs.forceConfirmInputRef?.$el?.focus?.() ?? this.$refs.forceConfirmInputRef?.focus?.();
                });
            }
        },
    },
};
</script>

<style scoped>
::v-deep .evidence-force-modal .modal-content {
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

::v-deep .evidence-force-modal .modal-body {
    padding: 0;
}

.evidence-modal-content {
    padding: 28px 24px 24px;
}

.evidence-modal-header {
    text-align: center;
    margin-bottom: 20px;
}

.evidence-modal-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 24px;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.35);
}

.evidence-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 6px;
}

.evidence-modal-subtitle {
    font-size: 14px;
    font-weight: 400;
    color: #64748b;
    margin: 0;
}

.evidence-modal-detail-box {
    background: #f8fafc;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 18px;
}

.evidence-modal-detail-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
}

.evidence-modal-bullet {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #3b82f6;
    flex-shrink: 0;
}

.evidence-modal-detail-text {
    font-size: 13px;
    color: #475569;
    line-height: 1.55;
    margin: 0 0 12px;
}

.evidence-modal-warning {
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
}

.evidence-modal-instruction {
    font-size: 13px;
    color: #475569;
    margin: 0 0 10px;
}

.evidence-modal-confirm-highlight {
    color: #d97706;
    font-weight: 700;
}

.evidence-modal-input {
    margin-bottom: 20px;
    border-radius: 10px;
    border-color: #e2e8f0;
    padding: 10px 14px;
}

.evidence-modal-input:focus {
    border-color: #d97706;
    box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.2);
}

.evidence-modal-footer {
    display: flex;
    justify-content: center;
    gap: 10px;
}

.evidence-modal-footer .btn-warning {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border: none;
    color: #fff;
}

.evidence-modal-footer-btn {
    padding: 8px 16px;
}
</style>
